import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/generals/section-heading'
import { buttonClasses } from '@/components/generals/button-link'
import { APP_SCREENS, external } from '@/constants/leesritme'

export const AppShowcase: FC = (): ReactNode => {
    const [active, setActive] = useState(0)
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const total = APP_SCREENS.length
    const current = APP_SCREENS[active]

    const select = (index: number, focus = false): void => {
        const next = (index + total) % total

        setActive(next)

        if (focus) tabRefs.current[next]?.focus()
    }

    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault()
            select(index + 1, true)
        }

        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault()
            select(index - 1, true)
        }

        if (event.key === 'Home') {
            event.preventDefault()
            select(0, true)
        }

        if (event.key === 'End') {
            event.preventDefault()
            select(total - 1, true)
        }
    }

    return (
        <section id='app' className='relative isolate overflow-hidden bg-ink py-20 text-paper sm:py-28'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -top-40 left-1/2 size-[50rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_center,rgb(234_111_75/0.18),transparent_60%)]' />
                <div className='absolute right-0 bottom-0 size-[30rem] rounded-full bg-[radial-gradient(circle_at_center,rgb(29_106_91/0.35),transparent_65%)]' />
            </div>

            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        tone='light'
                        eyebrow='Zo ziet een kind Leesritme'
                        title={(
                            <>
                                Elke dag lezen, <span className='font-medium text-sun italic'>met hulp waar het nodig is.</span>
                            </>
                        )}
                        intro='Echte schermen uit de leesapp. Groep 3 tot en met 8 leest in de app, groep 1 en 2 krijgen voorleesverhalen en een klankspel.'
                    />

                    <a href={external('/demo')} className={buttonClasses('outline-light', 'md', 'shrink-0 self-start lg:self-auto')}>
                        Probeer de leerlingdemo
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                    </a>
                </div>

                <div className='mt-14 grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_23rem] lg:gap-16 xl:grid-cols-[minmax(0,1fr)_25rem]'>
                    <div
                        role='tablist'
                        aria-label='Onderdelen van de leesapp'
                        className='-mx-5 flex snap-x gap-2 overflow-x-auto px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 xl:grid-cols-3'
                    >
                        {APP_SCREENS.map((screen, index) => {
                            const Icon = screen.icon
                            const selected = index === active

                            return (
                                <button
                                    key={screen.id}
                                    ref={element => {
                                        tabRefs.current[index] = element
                                    }}
                                    type='button'
                                    role='tab'
                                    id={`app-tab-${screen.id}`}
                                    aria-selected={selected}
                                    aria-controls='app-scherm'
                                    tabIndex={selected ? 0 : -1}
                                    onClick={() => select(index)}
                                    onKeyDown={event => onKeyDown(event, index)}
                                    className={cn(
                                        'group flex w-[15.5rem] shrink-0 snap-start flex-col items-start gap-3 rounded-2xl border p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sun sm:w-auto',
                                        selected
                                            ? 'border-sun/60 bg-white/10 shadow-[0_0_0_1px_rgb(246_207_93/0.35)]'
                                            : 'border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]'
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'grid size-10 place-items-center rounded-xl transition',
                                            selected ? 'bg-sun text-ink' : 'bg-white/10 text-paper group-hover:bg-white/15'
                                        )}
                                    >
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <span>
                                        <span className='block font-heading text-lg leading-snug font-semibold'>
                                            {screen.title}
                                        </span>

                                        <span className='mt-1 block text-sm leading-relaxed text-[#c3cad8]'>
                                            {screen.text}
                                        </span>
                                    </span>
                                </button>
                            )
                        })}
                    </div>

                    <div className='order-first mx-auto w-full max-w-[19rem] lg:order-none lg:max-w-none'>
                        <div
                            id='app-scherm'
                            role='tabpanel'
                            aria-labelledby={`app-tab-${current.id}`}
                            className='rounded-[2.6rem] bg-[#0d1527] p-2.5 shadow-[0_40px_90px_-30px_rgb(0_0_0/0.7)] ring-1 ring-white/15'
                        >
                            <div className='relative aspect-[9/16.5] overflow-hidden rounded-[2.1rem] bg-[#fbf7ef]'>
                                {APP_SCREENS.map((screen, index) => (
                                    <img
                                        key={screen.id}
                                        src={screen.src}
                                        alt={index === active ? screen.alt : ''}
                                        aria-hidden={index !== active}
                                        loading='lazy'
                                        decoding='async'
                                        className={cn(
                                            'absolute inset-x-0 top-0 h-auto w-full transition-opacity duration-500',
                                            index === active ? 'opacity-100' : 'opacity-0'
                                        )}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className='mt-6 flex items-center justify-between gap-4'>
                            <button
                                type='button'
                                onClick={() => select(active - 1)}
                                aria-label='Vorig scherm'
                                className='grid size-11 place-items-center rounded-full border border-white/20 text-paper transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sun'
                            >
                                <ChevronLeft className='size-5' aria-hidden='true' />
                            </button>

                            <p className='text-center text-sm text-[#c3cad8]' aria-live='polite'>
                                <span className='font-semibold text-paper'>{current.title}</span>
                                <span className='mx-2 text-white/30'>·</span>
                                <span className='tabular-nums'>{active + 1} van {total}</span>
                            </p>

                            <button
                                type='button'
                                onClick={() => select(active + 1)}
                                aria-label='Volgend scherm'
                                className='grid size-11 place-items-center rounded-full border border-white/20 text-paper transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sun'
                            >
                                <ChevronRight className='size-5' aria-hidden='true' />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
